import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  GestureResponderEvent,
  Platform,
  Pressable,
  PointerEvent as RNPointerEvent,
  View,
} from 'react-native';
import { KeyboardShortcutBadge } from '@sales-portal/components/keyboard-shortcut-badge';
import { Popover } from '@usepepper/sprout-kit-ui';
import {
  PricingEditField,
  PricingInvoiceHistoryEntry,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingInvoiceHistoryTable } from './pricing-invoice-history-table';
import { PricingTableQuickAction } from './pricing-table-quick-actions-popover';
import { Typography } from '@usepepper/ui/atoms/typography';

export interface PricingTableInputHistoryContext {
  enabled: boolean;
  field: PricingEditField;
  history: PricingInvoiceHistoryEntry[];
}

// Popover.Anchor forwards unknown props to PopperAnchor, whose `virtualRef`
// is measured in place of the anchor's own view; Tamagui's Trigger anchors the
// same way. The sk-ui typing stops at YStack props, hence the widening.
type AnchorProps = React.ComponentProps<typeof Popover.Anchor> & {
  virtualRef?: React.RefObject<View | null>;
};

interface Props {
  /** The input box to position against; it stays mounted outside the popover. */
  anchorRef: React.RefObject<View | null>;
  currencyCode: string;
  historyContext?: PricingTableInputHistoryContext;
  isOpen: boolean;
  isQuickActionsOpen: boolean;
  onActionPointerDown?: (event: RNPointerEvent) => void;
  onActionPressIn?: (event: GestureResponderEvent) => void;
  onActionSelect: (action: PricingTableQuickAction) => void;
  onOpenChange: (open: boolean) => void;
  quickActions?: PricingTableQuickAction[];
}

export const PricingTableInputPopover: React.FC<Props> = ({
  anchorRef,
  currencyCode,
  historyContext,
  isOpen,
  isQuickActionsOpen,
  onActionPointerDown,
  onActionPressIn,
  onActionSelect,
  onOpenChange,
  quickActions,
}) => {
  const [styles, theme] = useStyles();

  const showQuickActions = isQuickActionsOpen && Boolean(quickActions?.length);
  const showHistory = Boolean(historyContext?.enabled);

  const anchorProps: AnchorProps = { virtualRef: anchorRef };

  return (
    <Popover
      allowFlip
      offset={4}
      onOpenChange={onOpenChange}
      open={isOpen}
      // Native opens with the keyboard up, which the flip logic can't see
      placement={Platform.OS === 'web' ? 'bottom-start' : 'top-start'}
    >
      <Popover.Anchor {...anchorProps} />
      <Popover.Content disableFocusScope>
        <View style={styles.content}>
          {showQuickActions ? (
            <View style={styles.quickActionsSection}>
              {quickActions?.map((action) => {
                const isDisabled = Boolean(action.disabled);

                return (
                  <Pressable
                    disabled={isDisabled}
                    key={action.id}
                    onPointerDown={onActionPointerDown}
                    onPress={() => onActionSelect(action)}
                    onPressIn={onActionPressIn}
                    style={({ hovered }) => [
                      styles.quickActionPill,
                      hovered ? styles.quickActionPillHighlighted : null,
                    ]}
                  >
                    {action.shortcutKey ? (
                      <KeyboardShortcutBadge
                        shortcut={action.shortcutKey}
                        size={theme.sk.typography.paragraph.sm.lineHeight}
                      />
                    ) : null}
                    <Typography
                      color={
                        isDisabled
                          ? theme.sk.color.text.disabled
                          : theme.sk.color.text.primary
                      }
                      variant="paragraph-sm"
                    >
                      {action.label}
                    </Typography>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
          {showQuickActions && showHistory ? (
            <View style={styles.divider} />
          ) : null}
          {showHistory && historyContext ? (
            <PricingInvoiceHistoryTable
              currencyCode={currencyCode}
              field={historyContext.field}
              history={historyContext.history}
            />
          ) : null}
        </View>
      </Popover.Content>
    </Popover>
  );
};

const useStyles = createStyles((theme) => ({
  // Sections are narrower than the panel; keep them on the leading edge.
  content: {
    alignItems: 'flex-start',
  },
  divider: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.border.primary,
    height: 1,
  },
  quickActionPill: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  quickActionPillHighlighted: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  quickActionsSection: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    padding: theme.sk.spacing.spaceSmall,
  },
}));
